import { useState } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import PopoverButtonsGroup from 'components/new-ui/composed/popover-buttons-group/PopoverButtonsGroup';
import { Declaration } from 'store/declarations/declaration';
import { barcodeDisabled } from 'views/declarations/uk/gvms/components/barcode/BarcodeView';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import {
    ArchiveButton,
    BarcodeButton,
    CoreTemplateButton,
    DeleteButton,
    DocumentsButton,
    DuplicateButton,
    UnarchiveButton,
} from '../../dashboard-buttons';
import { ActionEvents } from '../../DeclarationColumns';

type Props = {
    declaration: Declaration;
    actions: ActionEvents;
    showActions: boolean;
    canDelete: boolean;
    canArchive: boolean;
};

const Actions = ({ declaration, actions, showActions, canDelete, canArchive }: Props) => {
    const [open, setOpen] = useState(false);

    const isArchived = Boolean(declaration.archived);

    // Closes the popover when an action is triggered
    const withClose =
        <T extends any[]>(fn?: (...args: T) => void) =>
        (...args: T) => {
            const e = args[0] as any;
            if (e && typeof e.stopPropagation === 'function') {
                e.stopPropagation();
            }

            fn?.(...args);
            setOpen(false);
        };

    if (!declaration.id) {
        return <EmptyCell dataTestId="declaration-actions-row" />;
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PopoverButtonsGroup
                open={open}
                onOpenChange={(next) => setOpen(next)}
                buttonEl={
                    <IconButton
                        icon={<MoreOutlined />}
                        tooltipText="Actions"
                        disabled={!showActions}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(true);
                        }}
                    />
                }
            >
                {actions.onDuplicate && declaration.declarationInternalType !== 'ARRIVAL' && (
                    <DuplicateButton record={declaration} onClick={withClose(actions.onDuplicate)} />
                )}
                {actions.onCoreTemplateToggle && (
                    <CoreTemplateButton record={declaration} onClick={withClose(actions.onCoreTemplateToggle)} />
                )}

                {actions.onArchive && !isArchived && (
                    <ArchiveButton disabled={!canArchive} record={declaration} onClick={withClose(actions.onArchive)} />
                )}
                {actions.onUnarchive && isArchived && (
                    <UnarchiveButton
                        disabled={!canArchive}
                        record={declaration}
                        onClick={withClose(actions?.onUnarchive)}
                    />
                )}

                {actions.onBarcode && (
                    <BarcodeButton
                        record={declaration}
                        onClick={withClose(actions.onBarcode)}
                        disabled={barcodeDisabled(declaration)}
                    />
                )}
                {actions.onPrint && <DocumentsButton record={declaration} onClick={withClose(actions.onPrint)} />}
                {actions.onDelete && (
                    <DeleteButton disabled={!canDelete} record={declaration} onClick={withClose(actions.onDelete)} />
                )}
            </PopoverButtonsGroup>
        </div>
    );
};

export default Actions;
